import { CACHE_TAG } from '@ahora-patagonia/contracts';
import { createApiClient } from '../shared/http/ApiClient.js';
import type { RouteLoader } from '../shared/ssr/RouteLoader.js';
import { headDeSeccion } from './seccion.head.js';
import { SeccionRepository } from './models/repositories/SeccionRepository.js';
import { SECCION_KIND, type SeccionData, type SeccionKind } from './models/entities/SeccionData.js';

export const ULTIMA_HORA_ROUTE_KEY = 'ultima-hora';
export const MAS_LEIDAS_ROUTE_KEY = 'mas-leidas';

/**
 * Loader de `/ultima-hora` y `/mas-leidas`. Uno por sección, armados con la misma fábrica: lo único
 * que cambia entre las dos es a qué endpoint le pegan.
 *
 * `?page=` es parte de la URL (indexable), igual que en una sección: la página 2 existe para el
 * crawler aunque el lector use "Ver más".
 *
 * **Sin 404**: estas dos páginas existen para cualquier medio. Una sin nada que mostrar cae en su
 * estado vacío, que es distinto de no existir — un 404 acá le diría al crawler que la borramos.
 *
 * Los tags son los mismos que emite la API para estas lecturas (`home` + `shell` + `ads`): la
 * página se refresca al publicar o archivar, y **una lectura del sitio no la purga** — el ranking de
 * Más leídas se mueve todo el tiempo y purgar por visita sería un miss por visita.
 */
function loaderDeSeccion(kind: SeccionKind): RouteLoader<SeccionData> {
  return async (ctx) => {
    const page = Math.max(1, Number(ctx.query['page']) || 1);

    const api = createApiClient({
      baseUrl: process.env['SSR_API_URL'] ?? 'http://localhost:3000',
      headers: { host: ctx.headers['host'] ?? '' },
    });

    const data = await new SeccionRepository(api).porPagina(kind, page);

    return {
      data,
      head: headDeSeccion(kind, data, ctx.site, page),
      cache: {
        sMaxAge: 60,
        staleWhileRevalidate: 300,
        tags: [CACHE_TAG.home, CACHE_TAG.shell, CACHE_TAG.ads],
        visibility: 'public',
      },
    };
  };
}

export const ultimaHoraLoader = loaderDeSeccion(SECCION_KIND.ultimaHora);
export const masLeidasLoader = loaderDeSeccion(SECCION_KIND.masLeidas);
